/* eslint-disable react-hooks/exhaustive-deps */
import _, { template } from 'lodash'
import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { Button, Checkbox, InformationToken, SearchBar } from '@intelie/ui'

import i18n from 'live/services/i18n'
import LiveTooltip from 'live/components/ui/tooltip'
import globalNotification from 'live/services/global-notification'

import ListActions from './components/ListActions'
import ListCheckbox from './components/ListCheckbox'
import { Template, TableTemplate } from '../../types'
import DeleteTemplateModal from './components/DeleteModal'
import { ANNOTATIONS_TEMPLATES_URL } from '../../constants'
import { ColorSquare, TemplateListContainer } from '../styles'
import {
    enableTemplate,
    disableTemplate,
    deleteTemplate,
    fetchTemplates,
    createTemplate,
    getTemplateLimit
} from '../../services/template'

const TemplateList: FC = () => {
    const [templates, setTemplates] = useState<Template[]>()
    const [filteredTemplates, setFilteredTemplates] = useState<TableTemplate[]>([])
    const [mapTemplatesToTable, setMapTemplatesToTable] = useState<TableTemplate[]>([])
    const [checkedTemplates, setCheckedTemplates] = useState<Template[]>([])
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [templateToDelete, setTemplateToDelete] = useState<Template>()
    const [templateLimit, setTemplateLimit] = useState<number>(-1)

    const handleSearchTemplate = (newSearchTerm: string): void => {
        setSearchTerm(newSearchTerm)

        if (!newSearchTerm) {
            setFilteredTemplates(mapTemplatesToTable)

            return
        }

        setFilteredTemplates(
            mapTemplatesToTable.filter(template => {
                return template.alias.toLowerCase().includes(newSearchTerm.toLowerCase())
            })
        )
    }

    const handleCheckAllTemplates = (): void => {
        if (checkedTemplates.length === mapTemplatesToTable.length) {
            setCheckedTemplates([])
        } else {
            setCheckedTemplates(mapTemplatesToTable)
        }
    }

    const handleEnableButton = (): boolean => {
        return checkedTemplates.some(template => !template.active)
    }

    const handleDisableButton = (): boolean => {
        return checkedTemplates.some(template => template.active)
    }

    const handleClickCheckbox = (template: Template, checked: boolean): void => {
        const isTemplateInCheckedArray = !!checkedTemplates.find(t => t.id === template.id)

        if (!isTemplateInCheckedArray && checked) {
            setCheckedTemplates([...checkedTemplates, template])
        } else if (isTemplateInCheckedArray && !checked) {
            setCheckedTemplates(checkedTemplates.filter(t => t.id !== template.id))
        }
    }

    const handleEnableCheckedTemplates = (): void => {
        // Filter to make request only for the inactive templates
        const inactiveTemplates = checkedTemplates?.filter(template => !template.active)

        try {
            inactiveTemplates.forEach(inactiveTemplate => {
                enableTemplate(inactiveTemplate.id)
            })
        } catch (error) {
            globalNotification.error('Failed to enable templates', 5)

            return
        }

        setTemplates(
            templates?.map(template => {
                const checkedTemplate = checkedTemplates.find(t => t.id === template.id)

                if (checkedTemplate) {
                    return { ...template, active: true }
                }

                return template
            })
        )

        setCheckedTemplates([])

        globalNotification.success(i18n('Templates enabled successfully'), 5)
    }

    const handleDisableCheckedTemplates = (): void => {
        // Filter to make request only for the active templates
        const activeTemplates = checkedTemplates?.filter(template => template.active)

        try {
            activeTemplates.forEach(activeTemplate => {
                disableTemplate(activeTemplate.id)
            })
        } catch (error) {
            globalNotification.error('Failed to disable templates', 5)

            return
        }

        setTemplates(
            templates?.map(template => {
                const checkedTemplate = checkedTemplates.find(t => t.id === template.id)

                if (checkedTemplate) {
                    return { ...template, active: false }
                }

                return template
            })
        )

        setCheckedTemplates([])

        globalNotification.success(i18n('Templates disabled successfully'), 5)
    }

    const handleDuplicateTemplate = async (template: Template): Promise<void> => {
        try {
            const newTemplate = await createTemplate(template)

            setTemplates([...templates, newTemplate])

            globalNotification.success(i18n('Template duplicated successfully'), 5)
        } catch (error) {
            globalNotification.error(i18n(error.message), 5)
        }
    }

    const handleDeleteTemplate = async (template: Template): Promise<void> => {
        try {
            await deleteTemplate(template.id)

            setTemplates(templates?.filter(t => t.id !== template.id))

            setTemplateToDelete({} as Template)
            setShowDeleteModal(false)

            globalNotification.success(i18n('Template deleted successfully'), 5)
        } catch (error) {
            globalNotification.error(i18n(error.message), 5)
        }
    }

    const handleDeleteModal = (template: Template): void => {
        setTemplateToDelete(template)
        setShowDeleteModal(true)
    }

    const handleMapTemplatesToTable = (): void => {
        const map = templates?.map(template => ({
            ...template,
            checkbox: (
                <ListCheckbox
                    checked={!!checkedTemplates.find(checkedTemplate => checkedTemplate.id === template.id)}
                    onChange={(checked: boolean) => handleClickCheckbox(template, checked)}
                />
            ),
            actions: (
                <ListActions
                    template={template}
                    handleDelete={handleDeleteModal}
                    handleDuplicate={handleDuplicateTemplate}
                />
            )
        }))

        if (map) {
            setMapTemplatesToTable(map)
        }
    }

    const handleUpdateFilteredTemplates = (): void => {
        setFilteredTemplates(
            mapTemplatesToTable.filter(template => {
                return template.alias.toLowerCase().includes(searchTerm.toLowerCase())
            })
        )
    }

    const openEditor = (edit = false, template?: Template): void => {
        if (edit && template) {
            window.location.href = `${ANNOTATIONS_TEMPLATES_URL}/${template.id}`

            return
        }

        window.location.href = `${ANNOTATIONS_TEMPLATES_URL}/new`
    }

    useEffect(() => {
        const fetchTemplatesList = async (): Promise<void> => {
            try {
                const templatesList = await fetchTemplates()

                setTemplates(templatesList)
            } catch (error) {
                globalNotification.error(i18n(error.message), 5)
            }
        }

        const fetchTemplateLimit = async (): Promise<void> => {
            try {
                const { templateLimit } = await getTemplateLimit()

                setTemplateLimit(templateLimit)
            } catch (error) {
                globalNotification.error(i18n(error.message), 5)
            }
        }

        fetchTemplatesList()
        fetchTemplateLimit()
    }, [])

    useEffect(() => {
        if (templates) {
            handleMapTemplatesToTable()
        }
    }, [templates, checkedTemplates])

    useEffect(() => {
        if (mapTemplatesToTable) {
            handleUpdateFilteredTemplates()
        }
    }, [mapTemplatesToTable])

    return (
        <TemplateListContainer>
            <div className="page-header">
                <LiveTooltip
                    position="right"
                    hide={templateLimit > templates?.length}
                    content={i18n('The number of templates has reached a maximum')}
                >
                    <Button
                        variant="primary"
                        icon="plus-circle"
                        className="new-template-button"
                        disabled={templateLimit <= templates?.length}
                        onClick={() => openEditor()}
                    >
                        {`${i18n('New template')} (${templates?.length}/${templateLimit})`}
                    </Button>
                </LiveTooltip>

                <SearchBar
                    placeholder={`${i18n('Search for templates')}...`}
                    filteredValue={searchTerm}
                    handleFilter={handleSearchTemplate}
                ></SearchBar>
            </div>
            {templateLimit <= templates?.length && (
                <div className="templates-limit-info">
                    <InformationToken type="warning">
                        {i18n(
                            'The number of template has reached a maximum. Please reach to support to increase your number of templates.'
                        )}
                    </InformationToken>
                </div>
            )}
            <div className="templates-manage">
                <span className="results-number">{i18n('Showing: %n templates', filteredTemplates?.length)}</span>
                <div className="status-control">
                    <Button
                        size="sm"
                        variant="text"
                        onClick={handleEnableCheckedTemplates}
                        disabled={!handleEnableButton()}
                    >
                        {i18n('Enable')}
                    </Button>
                    <div className="divider"></div>
                    <Button
                        size="sm"
                        variant="text"
                        onClick={handleDisableCheckedTemplates}
                        disabled={!handleDisableButton()}
                    >
                        {i18n('Disable')}
                    </Button>
                </div>
            </div>
            <div className="templates-table">
                <table>
                    <thead>
                        <tr>
                            <th className="template-checkbox">
                                <Checkbox
                                    checked={checkedTemplates.length === mapTemplatesToTable.length}
                                    indeterminate={
                                        checkedTemplates.length > 0 &&
                                        checkedTemplates.length !== mapTemplatesToTable.length
                                    }
                                    onChange={handleCheckAllTemplates}
                                />
                            </th>
                            <th className="template-alias">{i18n('Alias')}</th>
                            <th className="template-status">Status</th>
                            <th className="template-color">{i18n('Color')}</th>
                            <th className="template-author">{i18n('Author')}</th>
                            <th className="template-lastModified">{i18n('Last modified at')}</th>
                            <th className="template-actions"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTemplates.map((template, index) => (
                            <tr key={index}>
                                <td className="template-checkbox">{template.checkbox}</td>
                                <td className="template-alias" onClick={() => openEditor(true, template)}>
                                    {template.alias}
                                </td>
                                <td className="template-status">
                                    {template.active ? (
                                        <>
                                            <i className="fas fa-circle enabled" />
                                            <span>{i18n('Enabled')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-circle disabled" />
                                            <span>{i18n('Disabled')}</span>
                                        </>
                                    )}
                                </td>
                                <td className="template-color">
                                    <ColorSquare color={template.color} title={template.color} />
                                </td>
                                <td className="template-author">{_.capitalize(template.author)}</td>
                                <td className="template-lastModified">
                                    {template.lastModified
                                        ? moment(template.lastModified).format(`DD/MM/yy [${i18n('at')}] HH:mm`)
                                        : '-'}
                                </td>
                                <td className="template-actions">{template.actions}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <DeleteTemplateModal
                template={templateToDelete}
                show={showDeleteModal}
                setShow={setShowDeleteModal}
                callbackDelete={handleDeleteTemplate}
            />
        </TemplateListContainer>
    )
}

export default TemplateList
